import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, PartyPopper } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { DisplayAd } from './components/AdSense';
import { getPageKeywords } from './utils/seoUtils';
import ConversionSettings from './components/ConversionSettings';
import ImageFormatTable from './components/ImageFormatTable';
import { Link } from 'react-router-dom';
import UploadedImagesTable from './components/UploadedImagesTable';
import ConvertedImagesTable from './components/ConvertedImagesTable';
import ImagePreview from './components/ImagePreview';
import FeaturedArticles from './components/FeaturedArticles';
import Benefits from './components/Benefits';
import ProcessFlow from './components/ProcessFlow';
import {
  ImageFormat,
  ScaleOption,
  UploadedImage,
  ConvertedImage,
  convertImage,
  downloadImage,
  downloadAllImages,
  estimateOutputSize,
  formatBytes
} from './services/imageService';

function App() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [convertedImages, setConvertedImages] = useState<ConvertedImage[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<ImageFormat>('webp');
  const [compressionLevel, setCompressionLevel] = useState<number>(80);
  const [scaleOption, setScaleOption] = useState<ScaleOption>(1);

  const [conversionProgress, setConversionProgress] = useState<Record<string, number>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [predictedSize, setPredictedSize] = useState<string | null>(null);
  const [showUploadArea, setShowUploadArea] = useState<boolean>(true);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMoreInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    return () => {
      uploadedImages.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
      });
      convertedImages.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
      });
    };
  }, []);

  useEffect(() => {
    if (uploadedImages.length > 0) {
      updatePredictedSize();
    }
  }, [selectedFormat, compressionLevel, uploadedImages.length]);

  useEffect(() => {
    if (uploadedImages.length > 0) {
      setShowUploadArea(false);
    }
  }, [uploadedImages.length]);

  useEffect(() => {
    if (convertedImages.length === 0 && uploadedImages.length === 0) {
      setShowUploadArea(true);
    }
  }, [convertedImages.length, uploadedImages.length]);

  // Listen for per-image conversion progress
  useEffect(() => {
    const handleProgress = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.fileName && typeof detail?.progress === 'number') {
        setConversionProgress(prev => ({ ...prev, [detail.fileName]: detail.progress }));
      }
    };

    window.addEventListener('conversionProgress', handleProgress);
    return () => window.removeEventListener('conversionProgress', handleProgress);
  }, []);

  const updatePredictedSize = useCallback(() => {
    if (uploadedImages.length === 0) {
      setPredictedSize(null);
      return;
    }

    const totalOriginalSize = uploadedImages.reduce((sum, img) => sum + img.size, 0);
    const estimatedSize = estimateOutputSize(totalOriginalSize, selectedFormat, compressionLevel);
    setPredictedSize(formatBytes(estimatedSize));
  }, [uploadedImages, selectedFormat, compressionLevel]);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      setUploadError('Please select valid image files (JPEG, PNG, etc.)');
      toast.error('Please select valid image files (JPEG, PNG, etc.)');
      return;
    }
    
    // Check file size limits
    const oversizedFiles = imageFiles.filter(file => file.size > 10 * 1024 * 1024); // 10MB limit
    if (oversizedFiles.length > 0) {
      setUploadError(`Some files exceed the 10MB size limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      toast.error(`Some files exceed the 10MB size limit: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    const newUploadedImages = imageFiles.map(file => ({
      id: `${file.name}-${Date.now()}`,
      file,
      size: file.size,
      previewUrl: URL.createObjectURL(file)
    }));
    
    setUploadedImages(prev => [...prev, ...newUploadedImages]);
    setShowUploadArea(false);
    setUploadError(null);
    
    // Reset both file inputs
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (uploadMoreInputRef.current) {
      uploadMoreInputRef.current.value = '';
    }
  }, []);

  // Paste from clipboard support
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const imageFiles: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          const file = items[i].getAsFile();
          if (file) imageFiles.push(file);
        }
      }

      if (imageFiles.length > 0) {
        e.preventDefault();
        handleFiles(imageFiles);
        toast.success(`${imageFiles.length} image(s) pasted from clipboard`);
      }
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handleFiles]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    handleFiles(e.target.files);
  }, [handleFiles]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only set dragging to false if we're leaving the drop zone
    if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleConvertImages = useCallback(async () => {
    if (uploadedImages.length === 0) return;
    
    setIsConverting(true);
    let newConvertedImages: ConvertedImage[] = [];

    try {
      setConversionProgress({});
      // Use Promise.allSettled instead of Promise.all to handle partial failures
      const convertedImagesPromises = uploadedImages.map(img => 
        convertImage(img.file, selectedFormat, compressionLevel, scaleOption));
      const results = await Promise.allSettled(convertedImagesPromises);
      
      // Process successful conversions
      newConvertedImages = results
        .filter((result): result is PromiseFulfilledResult<ConvertedImage> => result.status === 'fulfilled')
        .map(result => result.value);
      
      // Handle failed conversions
      const failedResults = results
        .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
        .map(result => (result as PromiseRejectedResult).reason);
      
      if (failedResults.length > 0) {
        console.error('Some images failed to convert:', failedResults);
        toast.error(`${failedResults.length} image(s) failed to convert. The rest were processed successfully.`);
      }
      
      // Clean up old preview URLs before updating state
      uploadedImages.forEach(img => {
        URL.revokeObjectURL(img.previewUrl);
      });
      
      // Update state in a single batch
      setConvertedImages(prev => [...prev, ...newConvertedImages]);
      setUploadedImages([]);
      setPredictedSize(null);
      setShowUploadArea(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to convert images';
      console.error('Error converting images:', errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsConverting(false);
      setConversionProgress({});
    }
  }, [uploadedImages, selectedFormat, compressionLevel, scaleOption]);

  const handleShowUploadArea = useCallback(() => {
    if (uploadMoreInputRef.current) {
      uploadMoreInputRef.current.click();
    }
  }, []);

  const handleRemoveUploadedImage = (id: string) => {
    setUploadedImages(prev => {
      const filtered = prev.filter(img => img.id !== id);
      const removed = prev.find(img => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      
      if (filtered.length === 0 && convertedImages.length === 0) {
        setShowUploadArea(true);
      }
      
      return filtered;
    });
  };

  const handleRemoveConvertedImage = (id: string) => {
    setConvertedImages(prev => {
      const filtered = prev.filter(img => img.id !== id);
      const removed = prev.find(img => img.id === id);
      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      
      if (filtered.length === 0 && uploadedImages.length === 0) {
        setShowUploadArea(true);
      }
      
      return filtered;
    });
  };

  const handleClearUploaded = useCallback(() => {
    uploadedImages.forEach(img => {
      URL.revokeObjectURL(img.previewUrl);
    });
    setUploadedImages([]);
    setPredictedSize(null);
    if (convertedImages.length === 0) {
      setShowUploadArea(true);
    }
  }, [convertedImages.length]);

  const handleClearConverted = useCallback(() => {
    convertedImages.forEach(img => {
      URL.revokeObjectURL(img.previewUrl);
    });
    setConvertedImages([]);
    if (uploadedImages.length === 0 && convertedImages.length === 0) {
      setShowUploadArea(true);
    }
  }, [uploadedImages.length]);

  const handleDownloadImage = useCallback(async (image: ConvertedImage) => {
    if (!image?.convertedBlob) {
      throw new Error('No converted data available for download');
    }
    
    try {
      await downloadImage(image);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to download image';
      console.error('Error downloading image:', errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  }, []);

  const handleDownloadAll = useCallback(async () => {
    if (convertedImages.length === 0) return;
    
    try {
      await downloadAllImages(convertedImages);
    } catch (error) {
      console.error('Error downloading all images:', error);
    }
  }, [convertedImages]);

  return (
    <>
      <Helmet>
        <title>ShiftPic - Free Image Converter</title>
        <meta name="description" content={t('app.description.default').substring(0, 160)} />
        <meta name="keywords" content={getPageKeywords('/')} />
        <link rel="canonical" href={`https://shiftpic.com/${lang || i18n.language.split('-')[0]}/`} />
        <meta property="og:title" content="ShiftPic - Free Image Converter" />
        <meta property="og:description" content={t('app.description.default').substring(0, 160)} />
        <meta property="og:url" content={`https://shiftpic.com/${lang || i18n.language.split('-')[0]}/`} />
        <meta name="twitter:title" content="ShiftPic - Free Image Converter" />
        <meta name="twitter:description" content={t('app.description.default').substring(0, 160)} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
          <div className="max-w-[800px] mx-auto py-8 text-center">
            <h1 className="flex items-center justify-center gap-3">
              {convertedImages.length > 0 ? (
                <>
                  <PartyPopper className="text-accent" size={32} aria-hidden="true" />
                  {t('app.converted')}
                </>
              ) : uploadedImages.length > 0 ? (
                t('app.title.configure')
              ) : (
                t('app.title.default')
              )}
            </h1>
          {/* Conditionally render the description */}
          {uploadedImages.length === 0 && convertedImages.length === 0 && (
            <p>
              {t('app.description.default')}
            </p>
          )}
          {uploadedImages.length > 0 && convertedImages.length === 0 && (
            <p>
              {t('app.description.configure')}
            </p>
          )}
          </div>
          
          {/* Hidden file input for "Upload more" button */}
          <input 
            type="file" 
            ref={uploadMoreInputRef}
            multiple 
            accept="image/*" 
            onChange={handleFileChange} 
            className="hidden" 
            aria-label="Upload more images"
          />
          
          {showUploadArea && (
            <section className="mb-8" aria-labelledby="upload-section">
              <div className="upload-card-container">
                <div className="upload-card-body">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="flex-1">
                      <div 
                        ref={dropZoneRef}
                        className={`upload-card-dropzone group ${isDragging ? 'dragging' : ''}`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            fileInputRef.current?.click();
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-labelledby="upload-heading"
                      >
                        <input 
                          type="file" 
                          ref={fileInputRef}
                          multiple 
                          accept="image/*" 
                          onChange={handleFileChange} 
                          className="hidden" 
                          aria-label="File upload"
                        />
                        <Upload
                          className="upload-card-icon"
                          size={48} 
                          aria-hidden="true" 
                        />
                        <h2 id="upload-heading" className="upload-card-title">
                          {t('app.dragDrop.part1')} <span className="upload-card-title-underline">{t('app.dragDrop.part2')}</span>
                        </h2>
                        {uploadError && (
                          <Link
                            to="/troubleshooting"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                            }}
                            aria-label="View troubleshooting guide"
                            className="mt-4 text-primary hover:underline text-sm"
                          >
                            Having trouble? View troubleshooting guide
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="upload-card-description">
                {t('app.supportedFormats')}
              </p>
              <ProcessFlow />
              <Benefits />
              <FeaturedArticles />
              <ImageFormatTable />
              
              {/* AdSense Display Ad */}
              <div className="max-w-4xl mx-auto my-8">
                <DisplayAd className="mx-auto" />
              </div>
            </section>
          )}

          {uploadedImages.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8 items-start">
              <div className="md:col-span-1">
                <ConversionSettings
                  selectedFormat={selectedFormat}
                  setSelectedFormat={setSelectedFormat}
                  compressionLevel={compressionLevel}
                  setCompressionLevel={setCompressionLevel}
                  scaleOption={scaleOption}
                  setScaleOption={setScaleOption}
                  predictedSize={predictedSize}
                  uploadedImages={uploadedImages}
                />
              </div>
              <div className="md:col-span-3">
                <UploadedImagesTable
                  images={uploadedImages}
                  onRemove={handleRemoveUploadedImage}
                  onPreview={setPreviewImage}
                  onUploadMore={handleShowUploadArea}
                  onConvert={handleConvertImages}
                  onClear={handleClearUploaded}
                  isConverting={isConverting}
                  selectedFormat={selectedFormat}
                  compressionLevel={compressionLevel}
                  conversionProgress={conversionProgress}
                />
              </div>
            </div>
          )}

          <ConvertedImagesTable
            images={convertedImages}
            onDownload={handleDownloadImage}
            onDownloadAll={handleDownloadAll}
            onRemove={handleRemoveConvertedImage}
            onClear={handleClearConverted}
            onPreview={setPreviewImage}
          />

          <ImagePreview
            previewImage={previewImage}
            onClose={(e) => {
              e.stopPropagation();
              setPreviewImage(null);
            }}
            onImageClick={(e) => e.stopPropagation()}
          />
      </div>
    </>
  );
}

export default App;